import { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";

export const authApi = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
  withCredentials: true,
});

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savedSlugs, setSavedSlugs] = useState(new Set());

  const loadSaved = useCallback(async () => {
    try {
      const r = await authApi.get("/me/saved-clubs");
      setSavedSlugs(new Set(r.data.slugs));
    } catch {
      setSavedSlugs(new Set());
    }
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const r = await authApi.get("/auth/me");
      setUser(r.data || null);
      if (r.data) loadSaved();
    } catch {
      setUser(null);
    }
    setLoading(false);
  }, [loadSaved]);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const signIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + window.location.pathname;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const signOut = async () => {
    try {
      await authApi.post("/auth/logout");
    } catch (e) {
      console.warn("Logout request failed:", e?.message || e);
    }
    setUser(null);
    setSavedSlugs(new Set());
  };

  const toggleSave = async (slug) => {
    const saved = savedSlugs.has(slug);
    const next = new Set(savedSlugs);
    if (saved) next.delete(slug);
    else next.add(slug);
    setSavedSlugs(next);
    try {
      if (saved) await authApi.delete(`/me/saved-clubs/${slug}`);
      else await authApi.post(`/me/saved-clubs/${slug}`);
    } catch {
      setSavedSlugs(new Set(savedSlugs));
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, savedSlugs, signIn, signOut, toggleSave, setUser, loadSaved }}>
      {children}
    </AuthContext.Provider>
  );
};
